// src/client/game/core/FoodSystem.ts

import { FoodKind, type FoodInstance, type GridPos } from "./types";

/**
 * Minimal view of the world that FoodSystem needs.
 * GameModel satisfies this structurally.
 */
export interface FoodWorld {
  width: number;
  height: number;
  snake: {
    segments: GridPos[];
  };
  foods: FoodInstance[];
  obstacles: Set<string>;
  /** Optional enemy snakes to avoid spawning food on top of them. */
  enemySnakes?: { segments: GridPos[] }[];
}

export type FoodSpawnTuning = {
  /** How often we attempt to spawn a new food (ms). */
  spawnEveryMs: number;
  /** Max concurrent foods on the board. */
  maxFoodOnBoard: number;
  /** Chooses which food kind to spawn when spawning is allowed. */
  pickFoodKind: () => FoodKind;
};

/**
 * Handles all food spawning & pacing logic.
 *
 * Step 15 wiring: this can be tuned at runtime by level configs
 * (spawn interval, max foods, and the food mix).
 */
export class FoodSystem {
  private elapsedSinceSpawnMs = 0;

  private tuning: FoodSpawnTuning = {
    spawnEveryMs: 900,
    maxFoodOnBoard: 2,
    pickFoodKind: () => FoodKind.Normal,
  };

  constructor(
    private readonly world: FoodWorld,
    private readonly rng: () => number
  ) {
    this.ensureMinFood();
  }

  /**
   * Update tuning (called by scene/level wiring).
   * This does not clear the board; caller can choose to do that.
   */
  setSpawnTuning(tuning: FoodSpawnTuning): void {
    // Clamp to avoid pathological values.
    const spawnEveryMs = Math.max(50, Math.floor(tuning.spawnEveryMs));
    const maxFoodOnBoard = Math.max(1, Math.floor(tuning.maxFoodOnBoard));

    this.tuning = {
      spawnEveryMs,
      maxFoodOnBoard,
      pickFoodKind: tuning.pickFoodKind,
    };

    // Reset timer so changes take effect immediately.
    this.elapsedSinceSpawnMs = 0;
    this.ensureMinFood();
  }

  /**
   * Tick food-related timers and spawning.
   */
  tick(deltaMs: number): void {
    this.elapsedSinceSpawnMs += deltaMs;

    // Always guarantee at least one food exists.
    this.ensureMinFood();

    // Level-paced spawning up to max foods.
    this.maybeSpawnMoreFoods();
  }

  /**
   * Spawn a specific food kind somewhere on an empty cell.
   */
  spawnFood(kind: FoodKind): void {
    const pos = this.findEmptyCell();
    this.world.foods.push({ id: kind, x: pos.x, y: pos.y });
  }

  /**
   * Reset internal timers & ensure there's food after a reset.
   * NOTE: tuning persists across resets.
   */
  reset(): void {
    this.elapsedSinceSpawnMs = 0;
    this.ensureMinFood();
  }

  // ---- Internal helpers ----

  private ensureMinFood(): void {
    const min = Math.min(1, this.tuning.maxFoodOnBoard);
    while (this.world.foods.length < min) {
      this.spawnFood(this.tuning.pickFoodKind());
    }
  }

  private maybeSpawnMoreFoods(): void {
    const { spawnEveryMs, maxFoodOnBoard } = this.tuning;

    while (
      this.world.foods.length < maxFoodOnBoard &&
      this.elapsedSinceSpawnMs >= spawnEveryMs
    ) {
      this.elapsedSinceSpawnMs -= spawnEveryMs;
      this.spawnFood(this.tuning.pickFoodKind());
    }
  }

  private findEmptyCell(): GridPos {
    const { width, height, snake, foods, obstacles, enemySnakes = [] } = this.world;

    while (true) {
      const x = Math.floor(this.rng() * width);
      const y = Math.floor(this.rng() * height);
      const key = `${x},${y}`;

      const onSnake = snake.segments.some((s) => s.x === x && s.y === y);
      const onEnemy = enemySnakes.some((e) => e.segments.some((s) => s.x === x && s.y === y));
      const onFood = foods.some((f) => f.x === x && f.y === y);
      const onObstacle = obstacles.has(key);

      if (!onSnake && !onEnemy && !onFood && !onObstacle) {
        return { x, y };
      }
    }
  }
}
